import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ClipboardList, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ChecklistCard({ apiCall }) {
  const [data, setData] = useState({ items: [], total: 0, done: 0 });
  const [open, setOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeItem, setCompleteItem] = useState(null);
  const [completeForm, setCompleteForm] = useState({ note: "", image: "" });
  const [newItem, setNewItem] = useState({ title: "", schedule: "daily" });

  const load = async () => {
    try {
      const res = await apiCall("get", "/checklist/today");
      setData(res.data || { items: [], total: 0, done: 0 });
    } catch {
      /* silent */
    }
  };

  const loadAll = async () => {
    try {
      const res = await apiCall("get", "/checklist/items");
      setItems(res.data || []);
    } catch { /* silent */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleStart = (item) => {
    if (item.completed) {
      // toggle off
      apiCall("delete", `/checklist/items/${item.item_id}/complete`).then(load);
      return;
    }
    setCompleteItem(item);
    setCompleteForm({ note: "", image: "" });
    setCompleteOpen(true);
  };

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Imagem grande (max 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setCompleteForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleComplete = async () => {
    try {
      await apiCall("post", `/checklist/items/${completeItem.item_id}/complete`, {
        note: completeForm.note || null, image: completeForm.image || null,
      });
      toast.success("Item concluído");
      setCompleteOpen(false);
      load();
    } catch { toast.error("Erro"); }
  };

  const handleAddItem = async () => {
    if (!newItem.title) return;
    try {
      await apiCall("post", "/checklist/items", newItem);
      setNewItem({ title: "", schedule: "daily" });
      loadAll();
      load();
    } catch { toast.error("Erro"); }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm("Excluir esta rotina?")) return;
    await apiCall("delete", `/checklist/items/${id}`);
    loadAll();
    load();
  };

  const progress = data.total > 0 ? Math.round((data.done / data.total) * 100) : 0;

  return (
    <Card className="liquid-card p-5 rounded-2xl" data-testid="checklist-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="rounded-full bg-emerald-100 p-1.5">
          <ClipboardList className="h-4 w-4" strokeWidth={1.5} style={{ color: "#059669" }} />
        </div>
        <div className="text-xs uppercase tracking-widest text-foreground/60 font-heading font-medium">Checklist Diário</div>
        <span className="text-xs text-muted-foreground ml-1">{data.done}/{data.total} · {progress}%</span>
        <Button size="sm" variant="ghost" className="ml-auto text-xs" onClick={() => { setManageOpen(true); loadAll(); }} data-testid="manage-checklist-btn">
          Configurar
        </Button>
      </div>
      <div className="w-full h-1.5 rounded-full bg-emerald-100 mb-3 overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
      </div>
      {data.items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-3 text-center border border-dashed rounded">
          Configure rotinas diárias (caixa, limpeza, conferência de estoque...)
        </div>
      ) : (
        <div className="space-y-1">
          {data.items.map((it) => (
            <div key={it.item_id} className="flex items-center gap-2 p-2 rounded-md hover:bg-emerald-50/40 transition-colors" data-testid={`chk-${it.item_id}`}>
              <Checkbox checked={it.completed} onCheckedChange={() => handleStart(it)} />
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${it.completed ? "line-through text-muted-foreground" : ""}`}>{it.title}</div>
                {it.completed && (
                  <div className="text-[10px] text-muted-foreground">
                    ✓ {it.completed_by_name}
                    {it.note && <span className="ml-1">· {it.note}</span>}
                  </div>
                )}
              </div>
              {it.image && <img src={it.image} alt="" className="h-8 w-8 rounded object-cover" />}
            </div>
          ))}
        </div>
      )}

      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Concluir: {completeItem?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <div>
              <Label className="text-xs">Anotação (opcional)</Label>
              <textarea value={completeForm.note} onChange={(e) => setCompleteForm({ ...completeForm, note: e.target.value })} rows={3} className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" data-testid="chk-note-input" />
            </div>
            <div>
              <Label className="text-xs">Foto/documento (opcional)</Label>
              <Input type="file" accept="image/*" onChange={handleImage} data-testid="chk-image-input" />
              {completeForm.image && <img src={completeForm.image} alt="" className="mt-2 rounded max-h-32" />}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Cancelar</Button>
            <Button onClick={handleComplete} data-testid="chk-submit">Concluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Configurar rotinas</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {items.map((it) => (
                <div key={it.item_id} className="flex items-center gap-2 p-2 border border-border rounded">
                  <div className="flex-1">
                    <div className="text-sm">{it.title}</div>
                    <div className="text-[10px] text-muted-foreground">{it.schedule}</div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteItem(it.item_id)}>
                    <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                  </Button>
                </div>
              ))}
            </div>
            <div className="border-t pt-3">
              <Label className="text-xs">Nova rotina</Label>
              <div className="flex gap-2">
                <Input value={newItem.title} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} placeholder="Ex: Conferir caixa" data-testid="new-chk-input" />
                <select value={newItem.schedule} onChange={(e) => setNewItem({ ...newItem, schedule: e.target.value })} className="h-9 rounded-md border border-input bg-transparent px-2 text-sm">
                  <option value="daily">Diária</option>
                  <option value="weekly">Semanal</option>
                  <option value="monthly">Mensal</option>
                </select>
                <Button onClick={handleAddItem} data-testid="add-chk-btn"><Plus className="h-3 w-3" strokeWidth={1.5} /></Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
